"use client"

import * as React from "react"
import { usePostHog } from "posthog-js/react"
import { FAQAccordion } from "@/components/brand-kit/faq-accordion"
import { buildBrandFaqs } from "@/lib/brands/brand-faqs"
import { brandCatalogSeeAllAction } from "@/lib/brands/collection-cta"

import { BrandHero } from "./_components/brand-hero"
import { TypeFilterPills } from "./_components/type-filter-pills"
import { ProductGrid } from "./_components/product-grid"
import { SectionHeader } from "./_components/section-header"
import { CollectionsList } from "./_components/collections-list"
import { ArticlesGrid } from "./_components/articles-grid"
import { ToolsGrid } from "./_components/tools-grid"
import { CtaBanner } from "./_components/cta-banner"
import { MoreBrandsStrip } from "./_components/more-brands-strip"
import { RelatedArticles } from "./_components/related-articles"
import { RecallRecord } from "./_components/recall-record"
import { BrandNumbersBand } from "./_components/brand-numbers-band"
import {
  type BrandData,
  type BrandProduct,
  type BrandStats,
  type BrandCollection,
  type BrandArticle,
  type BrandRecall,
  type BrandOfferSummary,
  type ConcreteCatalogType,
  type RelatedBrand,
  type TypeBucket,
} from "./_components/types"

interface BrandClientProps {
  brand: BrandData
  /** Products for the CURRENT page (after type filter + pagination). */
  products: BrandProduct[]
  /** The brand's live filter/part-family rows — see BrandPageData. For a
   *  filter-family brand (zero appliances) this IS the catalog; otherwise it
   *  renders as its own section under the appliance grid. */
  familyProducts: BrandProduct[]
  /** Real family total (the grid is capped at 48 rows). */
  familyCount: number
  stats: BrandStats
  brandCollections: BrandCollection[]
  hubSlug: string | null
  collectionHref: string | null
  relatedBrands: RelatedBrand[]
  articles: BrandArticle[]
  cheapestProduct: { title: string; price: number } | null
  mostPopularType: string | null
  compactCount: number
  currentPage: number
  totalPages: number
  /** Active multi-select type filter parsed from the URL — empty = "All". */
  activeTypes: ConcreteCatalogType[]
  /** Counts per type bucket for the filter pills. */
  typeBuckets: TypeBucket[]
  /** Brand-agnostic articles for the bottom "Related Articles" strip. */
  relatedArticles: BrandArticle[]
  /** Curated CPSC notices for this brand (FRIDGE-401 Step 1) — Real-or-
   *  Hidden: the recall record section renders nothing when empty. */
  recalls: BrandRecall[]
  /** Live in-stock offer aggregate across tracked retailers (FRIDGE-401
   *  Step 1) — feeds the "by the Numbers" band; Real-or-Hidden. */
  offerSummary: BrandOfferSummary
}

const TIER_LABEL: Record<string, string> = {
  mainstream: "Popular",
  popular: "Popular",
  luxury: "Luxury",
  value: "Value",
  budget: "Budget",
  niche: "Niche",
}

/**
 * Orchestrator for the brand detail page redesign. Owns the FAQ generation
 * logic (which depends on multiple stats props) but otherwise just composes
 * the page-local section components from `./_components/` and the brand-kit
 * primitives (`FAQAccordion`, `Breadcrumb`, `HorizontalCarousel`,
 * `ProductCarouselCard`, `Pagination`).
 *
 * Type-filter + pagination state are URL-bound at the page level — this
 * component just renders what the server hands down.
 */
const PageContainer = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`mx-auto w-full max-w-[1280px] px-6 sm:px-8 lg:px-12 ${className}`}>
    {children}
  </div>
)

export default function BrandClient({
  brand,
  products,
  familyProducts,
  familyCount,
  stats,
  brandCollections,
  hubSlug,
  collectionHref,
  relatedBrands,
  articles,
  cheapestProduct,
  mostPopularType,
  compactCount,
  currentPage,
  totalPages,
  activeTypes,
  typeBuckets,
  relatedArticles,
  recalls,
  offerSummary,
}: BrandClientProps) {
  const posthog = usePostHog()
  React.useEffect(() => {
    if (posthog && brand) {
      posthog.capture("brand_page_viewed", {
        brand_slug: brand.slug,
        brand_name: brand.name,
        product_count: stats.productCount,
        tier: brand.tier,
        has_hub_page: !!hubSlug,
      })
    }
  }, [posthog, brand, stats.productCount, hubSlug])

  // FAQ — the ONE shared builder (lib/brands/brand-faqs.ts) also feeds the
  // FAQPage JSON-LD in components/seo/brand-schemas.tsx, so the visible
  // questions and the schema cannot drift (FRIDGE-257). Empty entries are
  // skipped inside the builder so brands missing certain fields just lose
  // those rows rather than rendering blanks.
  const faqs = React.useMemo(
    () => buildBrandFaqs({ brand, stats, cheapestProduct, mostPopularType, compactCount }),
    [brand, stats, cheapestProduct, mostPopularType, compactCount],
  )


  // Page-local container — matches the /brands index `PageContainer` so the
  // two surfaces feel like one system: 1280px max, generous edge padding.

  // suppress unused-var lint for TIER_LABEL when we don't need it inline
  void TIER_LABEL

  // Catalog URL base. Derived from the slug the server already resolved, NOT
  // from `usePathname()` — see the FRIDGE-237 note on the two consumers below.
  const catalogBasePath = `/brands/${brand.slug}`

  return (
    <div className="pb-20 lg:pb-24">
      <BrandHero
        brand={brand}
        catalogNoun={
          stats.productCount === 0 && familyProducts.length > 0
            ? "Refrigerator Filters & Parts"
            : undefined
        }
      />

      {/* "{Brand} by the Numbers" (FRIDGE-401 Step 1) — the live data band
          a single-store dealer page cannot match: full-catalog price median
          and range, cross-retailer offer counts, ENERGY STAR count, family
          breadth, capacity span. Real-or-Hidden inside the component; a
          filter-family or data-poor brand renders nothing here. */}
      <PageContainer className="mt-2">
        <BrandNumbersBand brandName={brand.name} stats={stats} offerSummary={offerSummary} />
      </PageContainer>

      {/* Type filter pills + catalog. Pills count APPLIANCE types, so a
          filter-family brand (zero appliances, family catalog only) hides
          them — an "All (0)" pill row filters nothing. */}
      {!(stats.productCount === 0 && familyProducts.length > 0) && (
        <PageContainer className="my-2">
          <TypeFilterPills buckets={typeBuckets} activeTypes={activeTypes} basePath={catalogBasePath} />
        </PageContainer>
      )}

      <PageContainer className="mt-8">
        <section id="brand-catalog" className="scroll-mt-[110px]">
          {/* Catalog title — reflects the multi-select filter. No types
              selected → "All Whirlpool Products". One type → "Whirlpool
              French Door". Two or more → "Whirlpool Products (3 types)"
              so the user knows the filter is active without us shoving a
              long comma-list into the H2. */}
          {stats.productCount === 0 && familyProducts.length > 0 ? (
            /* FRIDGE-431 sampled-brand find (08/23): a filter-family brand
               has no appliances, so the appliance grid rendered an empty
               catalog while its live /filter/ pages sat invisible. The
               family grid IS this brand's catalog — same cards, hrefs
               family-aware via productPagePath. */
            <>
              <SectionHeader
                eyebrow=""
                title={`All ${brand.name} Filters & Parts`}
              />
              <ProductGrid
                products={familyProducts}
                brandName={brand.name}
                currentPage={1}
                totalPages={1}
                basePath={catalogBasePath}
                activeTypes={[]}
              />
            </>
          ) : (
            <>
              <SectionHeader
                eyebrow=""
                title={(() => {
                  if (activeTypes.length === 0) return `All ${brand.name} Products`
                  if (activeTypes.length === 1) {
                    const label = typeBuckets.find(b => b.key === activeTypes[0])?.label ?? ""
                    return `${brand.name} ${label}`.trim()
                  }
                  return `${brand.name} Products (${activeTypes.length} types)`
                })()}
                // FRIDGE-1261: collectionHref is null when no verified destination
                // exists (FRIDGE-1053); a null href threw inside next/link and 500'd
                // the page. The builder offers "See all" only for a real destination.
                action={brandCatalogSeeAllAction(stats.productCount, products.length, collectionHref)}
              />
              <ProductGrid
                products={products}
                brandName={brand.name}
                currentPage={currentPage}
                totalPages={totalPages}
                basePath={catalogBasePath}
                activeTypes={activeTypes}
              />
            </>
          )}
        </section>

        {/* Appliance brands that ALSO make filters/parts get the family as
            its own section under the catalog — page 1 only, so the section
            never interleaves with appliance pagination. */}
        {stats.productCount > 0 && familyProducts.length > 0 && currentPage === 1 && activeTypes.length === 0 && (
          <section id="brand-filters-parts" className="mt-12 scroll-mt-[110px]">
            <SectionHeader
              eyebrow=""
              title={`${brand.name} Filters & Parts${familyCount > familyProducts.length ? ` (${familyCount})` : ""}`}
            />
            <ProductGrid
              products={familyProducts}
              brandName={brand.name}
              currentPage={1}
              totalPages={1}
              basePath={catalogBasePath}
              activeTypes={[]}
            />
          </section>
        )}
      </PageContainer>

      {/* FRIDGE-1085: the grouped catalog replaces the repeated mixed rails.
          Their price-based selection supported neither rating nor view claims,
          and repeating appliances below parts reversed the owner order. */}

      <PageContainer>
        <ArticlesGrid brandName={brand.name} articles={articles} />

        {/* The brand's official CPSC recall record (FRIDGE-401 Step 1) —
            the differentiator a regional dealer's `-overview` page cannot
            match. Real-or-Hidden: renders nothing for the (majority of)
            brands with no curated notice. */}
        <RecallRecord brandName={brand.name} recalls={recalls} />

        <ToolsGrid />

        {faqs.length > 0 && (
          <section className="my-12">
            <SectionHeader eyebrow="Common questions" title="Frequently Asked Questions" />
            <FAQAccordion
              items={faqs}
              columns={2}
              defaultOpen={[0, 1]}
              title=""
            />
          </section>
        )}

        <CtaBanner brandName={brand.name} href={collectionHref} />

        <MoreBrandsStrip brands={relatedBrands} />

        <RelatedArticles brandName={brand.name} articles={relatedArticles} />

        {/* Collections moved to the very bottom — the link list is a SEO /
            "browse more" surface, not a primary shopping path. Lives above
            the footer on both desktop and mobile so it acts as a graceful
            offramp after the visitor has worked through the catalog,
            carousels, CTA, related brands, and articles. */}
        <CollectionsList brandName={brand.name} collections={brandCollections} />
      </PageContainer>
    </div>
  )
}
